import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useAxiosWithAuth } from '../../../common/api/hooks/useAxiosWithAuth';
import { URL } from '../../../common/api/urls';

const FYERS_BROKER = 'fyers';

const PlaceOrderModal = ({ open, onClose, instrument, onOpenBrokerLogin, onOrderSuccess, onOrderError }) => {
  const [, connectionsResponse, connectionsError, connectionsLoaded, resetConnections, fetchConnections] = useAxiosWithAuth();
  const [, orderResponse, orderError, orderLoaded, resetOrder, submitOrder] = useAxiosWithAuth();

  const [side, setSide] = useState('buy');
  const [lots, setLots] = useState(1);
  const [price, setPrice] = useState('');
  const [isMarketOrder, setIsMarketOrder] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const wasOpenRef = useRef(false);

  const lotSize = instrument?.ls ? Number(instrument.ls) : 0;
  const totalQuantity = lots > 0 && lotSize > 0 ? lots * lotSize : 0;

  const fyersConnection = useMemo(() => {
    const connections = connectionsResponse?.connections;
    if (!Array.isArray(connections)) {
      return null;
    }
    return connections.find(
      (conn) => conn.broker_name === FYERS_BROKER && conn.can_place_orders
    ) ?? null;
  }, [connectionsResponse]);

  useEffect(() => {
    if (!open || !instrument) {
      return;
    }

    setSide('buy');
    setLots(1);
    setIsMarketOrder(false);
    setFormError('');
    setSubmitting(false);
    setPrice(
      instrument.close !== null && instrument.close !== undefined && instrument.close !== ''
        ? String(Number(instrument.close))
        : ''
    );
  }, [open, instrument]);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }

    if (!instrument || wasOpenRef.current) {
      return;
    }

    wasOpenRef.current = true;
    resetConnections();
    resetOrder();
    fetchConnections(URL.BROKER_CONNECTIONS, 'GET', {});
  }, [open, instrument]);

  useEffect(() => {
    if (!orderLoaded) {
      return;
    }

    setSubmitting(false);

    if (orderResponse?.success) {
      resetOrder();
      return;
    }

    const errorMessage = orderResponse?.message
      || orderError?.response?.data?.message
      || 'Unable to place order. Please try again.';

    if (open) {
      setFormError(errorMessage);
    } else {
      onOrderError?.(errorMessage);
    }
    resetOrder();
  }, [orderLoaded, orderResponse, orderError, open, onOrderError, resetOrder]);

  const handleSubmit = () => {
    setFormError('');

    if (!fyersConnection) {
      setFormError('Connect your Fyers account to place orders.');
      return;
    }

    if (!lotSize || lotSize <= 0) {
      setFormError('Lot size is unavailable for this instrument.');
      return;
    }

    if (!lots || lots < 1) {
      setFormError('Enter a valid quantity in lots.');
      return;
    }

    if (!isMarketOrder) {
      const limitPrice = Number(price);
      if (!price || Number.isNaN(limitPrice) || limitPrice <= 0) {
        setFormError('Enter a valid limit price.');
        return;
      }
    }

    const orderId = crypto.randomUUID();
    const payload = {
      order_id: orderId,
      broker_name: FYERS_BROKER,
      instrument: instrument.symbol,
      side,
      quantity: lots * lotSize,
      product: 'INTRADAY',
      order_kind: isMarketOrder ? 'MARKET' : 'LIMIT',
      validity: 'IOC',
    };

    if (!isMarketOrder) {
      payload.limit_price = Number(price);
    }

    onOrderSuccess?.('Order placed');
    onClose();
    submitOrder(URL.BROKER_ORDER_PREVIEW, 'POST', payload);
  };

  const instrumentLabel = [instrument?.displayLongName, instrument?.series]
    .filter(Boolean)
    .join(' ');

  const connectionsLoading = open && !connectionsLoaded;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // maxWidth="xs"
      // fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: '#0f1f3d',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          width: "100%", maxWidth: "500px"
        },
      }}
    >
      <DialogTitle sx={{ color: 'common.white', fontWeight: 700 }}>
        Place Order
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Instrument
          </Typography>
          <Typography sx={{ color: 'common.white', fontWeight: 600 }}>
            {instrumentLabel || instrument?.symbol}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Broker
          </Typography>
          {connectionsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Checking broker connection...
              </Typography>
            </Box>
          ) : fyersConnection ? (
            <Typography sx={{ color: 'common.white' }}>
              Fyers ({fyersConnection.masked_account || 'connected'})
            </Typography>
          ) : (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="body2" sx={{ color: '#ffb4a9', mb: 1 }}>
                {connectionsError?.response?.data?.message
                  || 'Fyers is not connected. Log in with your broker to place orders.'}
              </Typography>
              {onOpenBrokerLogin ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onOpenBrokerLogin}
                  sx={{ textTransform: 'none', color: 'common.white', borderColor: 'rgba(255,255,255,0.3)' }}
                >
                  Connect Fyers
                </Button>
              ) : null}
            </Box>
          )}
        </Box>

        <Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.5, display: 'block' }}>
            Side
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={side}
            onChange={(_, value) => value && setSide(value)}
            sx={{
              '& .MuiToggleButton-root': {
                color: 'rgba(255,255,255,0.7)',
                borderColor: 'rgba(255,255,255,0.2)',
                textTransform: 'none',
                px: 2,
              },
              '& .Mui-selected': {
                color: 'common.white !important',
                bgcolor: side =='buy' ? 'rgba(83, 148, 240, 0.5) !important': 'rgba(218, 99, 99, 0.6) !important',
              },
            }}
          >
            <ToggleButton value="buy">Buy</ToggleButton>
            <ToggleButton value="sell">Sell</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <TextField
          label="Quantity (lots)"
          type="number"
          size="small"
          value={lots}
          onChange={(e) => setLots(Math.max(1, parseInt(e.target.value, 10) || 1))}
          inputProps={{ min: 1, step: 1 }}
          helperText={
            lotSize > 0
              ? `= ${totalQuantity} units (lot size ${lotSize})`
              : 'Lot size unavailable'
          }
          FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.55)' } }}
          InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.7)' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              color: 'common.white',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
            },
          }}
        />

        <TextField
          label="Price (LTP)"
          type="number"
          size="small"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          disabled={isMarketOrder}
          inputProps={{ min: 0, step: 0.05 }}
          helperText={
            side == 'buy' && !isMarketOrder
              ? `= ${totalQuantity * price} Rs capital required`
              : ''
          }
          InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.7)' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              color: 'common.white',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
            },
          }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={isMarketOrder}
              onChange={(e) => setIsMarketOrder(e.target.checked)}
              sx={{ color: 'rgba(255,255,255,0.6)', '&.Mui-checked': { color: '#62a0ff' } }}
            />
          }
          label="Market"
          sx={{ color: 'rgba(255,255,255,0.85)', m: 0 }}
        />

        {formError ? (
          <Typography variant="body2" sx={{ color: '#ffb4a9' }}>
            {formError}
          </Typography>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.8)' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || connectionsLoading || !fyersConnection || !lotSize}
          sx={{
            textTransform: 'none',
            bgcolor: '#62a0ff',
            '&:hover': { bgcolor: '#4f8df0' },
          }}
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Place Order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlaceOrderModal;
